/**
 * Render helper for Solid `createResource` / API calls. Renders
 * a spinner while pending, an error box on failure, and the
 * children when data arrives. One place for loading/error UX
 * across the whole admin panel.
 *
 * The children read the value through Solid's accessor, so a value
 * written back with `mutate` reaches a child that stays mounted.
 */

import { Accessor, JSX, Match, Resource, Switch } from "solid-js";

import { ApiError } from "../lib/api";

export default function AsyncResource<T>(props: {
  resource: Resource<T | undefined>;
  children: (value: Accessor<T>) => JSX.Element;
}) {
  return (
    <Switch>
      <Match when={props.resource.loading}>
        <div class="text-sm text-slate-500 py-6">Loading…</div>
      </Match>
      <Match when={props.resource.error}>
        {(err) => {
          const e = err() as unknown;
          if (e instanceof ApiError) {
            if (e.isForbidden) {
              return (
                <div class="text-sm text-slate-600 py-6">
                  You don't have access to this view.
                </div>
              );
            }
            if (e.isNotFound) {
              return (
                <div class="text-sm text-slate-600 py-6">Not found.</div>
              );
            }
            return (
              <div class="text-sm text-red-600 py-6">
                Request failed — {e.code}
                {e.detail ? `: ${e.detail}` : ""}
              </div>
            );
          }
          return (
            <div class="text-sm text-red-600 py-6">Something went wrong.</div>
          );
        }}
      </Match>
      <Match when={props.resource()}>
        {(value) => props.children(value)}
      </Match>
    </Switch>
  );
}
